// Volts protokol mellem browseren og /api/agent. Delt af klient og Worker, så begge sider kender
// de samme grænser. Ren TS uden DOM: Workeren importerer filen direkte.
//
// Klienten sender kun typede data (kandidater, en kompakt state og en enum for hændelsen). Alle
// prompts bygges på serveren fra faste skabeloner (worker/prompts.ts), så endpointet ikke kan
// bruges som gratis sprogmodel.

/** Hvad Volt beder om. `hands` er et alias for `act` (Jev vælger en handling). */
export type AgentKind = 'act' | 'hands' | 'card' | 'line' | 'plan' | 'chat';
export const AGENT_KINDS: readonly AgentKind[] = ['act', 'hands', 'card', 'line', 'plan', 'chat'];

export const LIMITS = {
  /** Højst så mange kandidater pr. kald (top 7 plus vent). */
  candidates: 8,
  /** Den serialiserede state må højst fylde så mange bytes. */
  stateBytes: 8 * 1024,
  /** Hele request-body'en. */
  bodyBytes: 12 * 1024,
  /** Spillerens fritekst klippes til så mange tegn. */
  text: 80,
  /** Navne og id'er i state. */
  name: 24,
  id: 64,
  cities: 30,
  risks: 12,
  threats: 6,
  recent: 6,
  /** Strategens note. */
  note: 60,
  /** Replikker: højst så mange ord. */
  words: 12,
  lineChars: 80,
} as const;

export const REGIONS = ['all', 'west', 'east', 'north', 'south'] as const;
export type Region = (typeof REGIONS)[number];
export const SAVE_FOR = ['nothing', 'storm', 'new_city', 'upgrade', 'battery'] as const;
export type SaveFor = (typeof SAVE_FOR)[number];
export const PRIORITIES = ['balanced', 'cities', 'green', 'safety', 'money'] as const;
export type Priority = (typeof PRIORITIES)[number];
/** Ting spilleren har sagt, Volt skal lade være. `wait`: "byg løs", Volt står ikke stille og sparer. */
export const AVOID = ['storm', 'cards', 'build', 'upgrade', 'spend', 'wait'] as const;
export type Avoid = (typeof AVOID)[number];
export const MOODS = ['happy', 'calm', 'worried', 'proud', 'cheeky'] as const;

/** Volts rolle, når der spilles med roller (spor "sandhed"): spilleren har den anden. */
export const VOLT_ROLES = ['planner', 'drift'] as const;
export type VoltRole = (typeof VOLT_ROLES)[number];
export type Mood = (typeof MOODS)[number];

/** Hvad der skete, når Volt skal sige noget. Workeren har en skabelon pr. hændelse. */
export const LINE_EVENTS = [
  'start',
  'built',
  'upgraded',
  'reinforced',
  'repaired',
  'saved',
  'blackout',
  'storm',
  'card',
  'year',
  'won',
  'lost',
  'idle',
  'player_built',
] as const;
export type LineEvent = (typeof LINE_EVENTS)[number];

export const ACTION_KINDS = [
  'build_line',
  'upgrade_line',
  'bury_line',
  'demolish',
  'reinforce_mast',
  'upgrade_station',
  'build_battery',
  'build_flywheel',
  'hire_crew',
  'dispatch_crew',
  'shoo_threat',
  'power_fcr',
  'power_norway',
  'power_shed',
  'pick_pack',
  'pick_both',
  'answer_card',
  'call_year',
  'trim_hz',
  'wait',
] as const;

export const THREAT_KINDS = [
  'storm',
  'digger',
  'anchor',
  'protest',
  'spike',
  'heatwave',
  'dc_rush',
  'plant_trip',
  'icing',
  'dunkelflaute',
  'cable_half',
  'wind_bonanza',
  'transformer',
  'smaa',
  'duo',
] as const;

export const RISK_WHY = ['storm', 'digger', 'anchor', 'overload', 'broken', 'single'] as const;

/** En kandidat på tråden: samme felter som sim'ens Candidate plus reservebottens score. */
export interface WireCandidate {
  id: string;
  kind: (typeof ACTION_KINDS)[number];
  target?: string;
  opt?: string;
  cost: number;
  /** Reservebottens nytte, afrundet. Et hint til Jev, ikke en ordre. */
  score: number;
  /** Navnene på de noder, kandidaten handler om (højst to). */
  names?: string[];
}

export interface Plan {
  focus_region: Region;
  save_for: SaveFor;
  priority: Priority;
  note: string;
}

export interface Policy {
  focus_region: Region;
  save_for: SaveFor;
  priority: Priority;
  avoid: Avoid[];
}

export interface WireCity {
  id: string;
  name: string;
  /** Forbrug og leveret i MW. */
  dem: number;
  srv: number;
  /** Mørkeur i procent. */
  clock: number;
  shield: number;
  region: Region;
}

export interface WireRisk {
  line: string;
  a: string;
  b: string;
  load: number;
  why: (typeof RISK_WHY)[number];
}

export interface WireThreat {
  id: string;
  kind: (typeof THREAT_KINDS)[number];
  /** Sekunder til den rammer (negativ: i gang). */
  in: number;
  lines: string[];
}

/** Den kompakte state, Jev og Haiku ser (højst 8 KB). Alle felter er kendte og typede. */
export interface WireState {
  level: string;
  year: number;
  season: number;
  budget: number;
  trust: number;
  green: number;
  coop: boolean;
  cities: WireCity[];
  risks: WireRisk[];
  threats: WireThreat[];
  plan?: Plan;
  policy?: Policy;
  recent?: string[];
  /** Volts rolle (planner bygger, drift passer nettet). Mangler den, må Volt alt. */
  role?: VoltRole;
}

export interface AgentRequest {
  kind: AgentKind;
  token: string;
  state: WireState;
  candidates?: WireCandidate[];
  /** Kun til `chat`: spillerens instruks. */
  text?: string;
  /** Kun til `line`: hvad der skete. */
  event?: LineEvent;
  /** Kun til `line`: hvem eller hvad det handler om (et navn fra kortet). */
  about?: string;
}

export interface ActAnswer {
  ok: true;
  source: 'jev';
  choice: string;
  confidence: number;
  probabilities: Record<string, number>;
  ms: number;
  slow?: boolean;
}

export interface LineAnswer {
  ok: true;
  source: 'haiku';
  line: string;
  mood: Mood;
  ms: number;
  slow?: boolean;
}

export interface PlanAnswer {
  ok: true;
  source: 'haiku';
  plan: Plan;
  line: string;
  ms: number;
  slow?: boolean;
}

export interface ChatAnswer {
  ok: true;
  source: 'haiku';
  reply: string;
  /** Den nye politik. Handlede ønsket ikke om spillet, er det den politik, klienten sendte. */
  policy: Policy;
  /** Ønsket handlede ikke om spillet: politikken er uændret. */
  off_topic?: true;
  ms: number;
  slow?: boolean;
}

export type FallbackReason = 'budget' | 'rate' | 'error' | 'timeout' | 'invalid' | 'session' | 'refusal' | 'offline';

export interface FallbackAnswer {
  fallback: true;
  reason: FallbackReason;
  /** Færdigskrevet replik, når Haiku afviste eller budgettet er brugt. */
  line?: string;
}

export type AgentAnswer = ActAnswer | LineAnswer | PlanAnswer | ChatAnswer | FallbackAnswer;

export const DEFAULT_POLICY: Policy = { focus_region: 'all', save_for: 'nothing', priority: 'balanced', avoid: [] };

/**
 * Ryd selvmodsigelser i en politik: "spar til stormen" og "jeg tager stormen" kan ikke gælde på
 * én gang (stormen vinder, den er spillerens), og "byg løs" slår "spar" og "byg ikke" ud.
 */
export function sanePolicy(p: Policy): Policy {
  const avoid = [...new Set(p.avoid)];
  const out: Policy = { focus_region: p.focus_region, save_for: p.save_for, priority: p.priority, avoid };
  if (avoid.includes('storm') && out.save_for === 'storm') out.save_for = 'nothing';
  if (avoid.includes('wait')) {
    if (out.priority === 'money') out.priority = 'balanced';
    out.save_for = 'nothing';
    out.avoid = avoid.filter((a) => a !== 'build' && a !== 'spend');
  }
  return out;
}

/** Har spilleren bedt om noget, der ikke er standard? */
export function isDefaultPolicy(p: Policy): boolean {
  return p.focus_region === 'all' && p.save_for === 'nothing' && p.priority === 'balanced' && p.avoid.length === 0;
}

/**
 * Haikus plan under spillerens ønske: spilleren har altid sidste ord. Hvor de to er uenige, vinder
 * politikken, og planens note droppes, så Volt ikke siger "hold kassen" mens han bygger løs.
 */
export function planUnder(plan: Plan | null, policy: Policy): Plan | null {
  if (!plan) return null;
  const out: Plan = { ...plan };
  if (policy.focus_region !== 'all') out.focus_region = policy.focus_region;
  if (policy.priority !== 'balanced') out.priority = policy.priority;
  const build = policy.avoid.includes('wait');
  if (build || policy.save_for !== 'nothing' || policy.priority === 'money') out.save_for = build ? 'nothing' : policy.save_for;
  if (build && out.priority === 'money') out.priority = 'balanced';
  if (policy.avoid.includes('storm') && out.save_for === 'storm') out.save_for = 'nothing';
  if (out.save_for !== plan.save_for || out.priority !== plan.priority || out.focus_region !== plan.focus_region) out.note = '';
  return out;
}

/** Klip en replik til højst `LIMITS.words` ord og `LIMITS.lineChars` tegn. Ingen tankestreger. */
export function clipLine(s: string): string {
  const clean = s
    .replace(/[–—]/g, ',')
    .replace(/\s+/g, ' ')
    .trim();
  const all = clean.split(' ').filter(Boolean);
  let words = all.slice(0, LIMITS.words).join(' ');
  const cut = all.length > LIMITS.words || words.length > LIMITS.lineChars;
  if (words.length > LIMITS.lineChars) words = words.slice(0, LIMITS.lineChars - 1).trimEnd();
  if (!cut) return words;
  // Klippet: slut ved sidste hele sætning, hvis der er en, frem for midt i et ord.
  const end = Math.max(words.lastIndexOf('. '), words.lastIndexOf('! '), words.lastIndexOf('? '));
  if (end >= 12) return words.slice(0, end + 1);
  if (/[.!?]$/.test(words)) return words;
  const comma = words.lastIndexOf(',');
  if (comma >= 20) return `${words.slice(0, comma)}.`;
  return `${words.replace(/[,;:]$/, '')}…`;
}

const EN_WORDS = new Set(['the', 'and', 'is', 'are', 'you', 'your', 'we', "let's", 'lets', "it's", 'this', 'that', 'with', 'great', 'nice', 'job', 'good', 'let', 'us', 'our', 'will', 'now', 'of', 'on', 'in', 'keep', 'grid', 'power', 'town', 'towns', 'focus', 'save', 'build', 'west', 'east']);
const DA_WORDS = new Set(['og', 'er', 'du', 'vi', 'det', 'en', 'et', 'på', 'til', 'med', 'jeg', 'ikke', 'så', 'nu', 'den', 'har', 'lad', 'os', 'godt', 'mig', 'din', 'vores', 'af', 'i', 'mod', 'skal']);

/**
 * Volt taler dansk. Haiku glider af og til over i engelsk eller siger "på dansk" højt; sådan en
 * tekst bliver til '' (så siger Volt ingenting i stedet). Ren heuristik: to engelske småord, der
 * er flere end de danske, eller et ord om selve sproget.
 */
export function danishOnly(s: string): string {
  if (/\b(danish|english|dansk|engelsk)\b/i.test(s)) return '';
  const words = s.toLowerCase().match(/[\p{L}']+/gu) ?? [];
  let en = 0;
  let da = 0;
  for (const w of words) {
    if (EN_WORDS.has(w)) en++;
    if (DA_WORDS.has(w)) da++;
  }
  return en >= 2 && en > da ? '' : s;
}

/** Normalisér sandsynligheder, så de summerer til 1 (Jev giver nogle gange 0,99). */
export function normalize(p: Record<string, number>): Record<string, number> {
  let sum = 0;
  for (const v of Object.values(p)) if (Number.isFinite(v) && v > 0) sum += v;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(p)) out[k] = sum > 0 && Number.isFinite(v) && v > 0 ? v / sum : 0;
  return out;
}
